import React from "react";
import { Accordion, Table } from "react-bootstrap";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  REMCHANNEL_COLORS,
  LEGEND_LABEL_OVERRIDES,
  formatLegendLabel,
  getHiddenSections,
  CHART_PALETTE,
} from "./reportTheme";

/**
 * Long-term incentive results: eligibility, plan types, performance
 * conditions, vesting methodology, vesting periods and settlement.
 * Reads only reportData plus the shared theme.
 */
const LtiResultsSection = ({ reportData }) => {
  const hiddenSections = getHiddenSections();

  return (
  <Accordion.Item eventKey="1">
    <Accordion.Header className="border-bottom shadow-sm bg-white sticky-top">
    LONG-TERM INCENTIVES (LTI) RESULTS
    </Accordion.Header>
    <Accordion.Body>
      {/* Eligibility to Participate
       * This chart displays the eligibility of employees to participate in Long-Term Incentive (LTI) plans
       * across different job grades. The data is presented as percentages showing what portion of employees
       * in each grade are eligible for LTI plans, categorized as 'All', 'Some', or 'None'.
       *
       * The vertical bars represent different job grades, while the colors indicate the eligibility categories.
       * Percentages are displayed on the bars when the value is significant enough to be visible.
       * This visualization helps identify patterns in LTI eligibility across the organization's hierarchy.
       *
       */}
      <h5>LTI eligibility</h5>
      <p>
      Participants reported eligibility for the company’s LTI schemes based on employee grade. They specified whether all, some, or none of the employees in each grade were eligible to participate in the scheme and provided the potential values possible at different performance levels across various percentiles per the table below.
      </p>
      {reportData.chartDataByGradingSystem.ltiEligibility && (
        <div style={{ height: "500px", marginBottom: "2rem", width: "100%" }}>
          {(() => {
            const original =
              reportData.chartDataByGradingSystem.ltiEligibility;
            const { labels, datasets } = original;

            const gradeMap = {};
            labels.forEach((grade, idx) => {
              if (!gradeMap[grade]) {
                gradeMap[grade] = [];
              }
              gradeMap[grade].push(idx);
            });

            const uniqueGrades = [];
            labels.forEach((grade) => {
              if (!uniqueGrades.includes(grade)) {
                uniqueGrades.push(grade);
              }
            });

            const combinedDatasets = datasets.map((dataset, dsIdx) => {
              // For each unique grade, sum the values from all indices where that grade appears
              const newData = uniqueGrades.map((grade) => {
                const indices = gradeMap[grade];
                // Sum the values for this dataset at all indices for this grade
                return indices.reduce(
                  (sum, idx) => sum + (dataset.data[idx] || 0),
                  0
                );
              });
              return {
                ...dataset,
                data: newData,
                backgroundColor:
                  CHART_PALETTE[dsIdx % CHART_PALETTE.length],
                borderColor:
                  CHART_PALETTE[dsIdx % CHART_PALETTE.length],
                borderWidth: 1,
              };
            });  

            combinedDatasets.forEach((ds) => {
              ds.data = ds.data.map((val) => (val > 100 ? 100 : val));
            });

            return (
              <Bar
                data={{
                  ...original,
                  labels: uniqueGrades,
                  datasets: combinedDatasets,
                }}
                options={{
                  ...reportData.eligibilityChartOptions,
                  responsive: true,
                  maintainAspectRatio: false,
                  indexAxis: "y",
                  scales: {
                    x: {
                      stacked: true,
                      max: 100,
                      title: {
                        display: true,
                        text: "Percentage of Participants",
                      },
                    },
                    y: {
                      title: {
                        display: true,
                        text: "Grades",
                      },
                    },
                  },
                  plugins: {
                    title: {
                      display: true,
                      text: "LTI Eligibility",
                      font: {
                        size: 16,
                      },
                    },
                    ...reportData.eligibilityChartOptions?.plugins,
                    legend: {
                      position: "bottom",
                    },
                    datalabels: {
                      ...reportData.eligibilityChartOptions?.plugins
                        ?.datalabels,
                      formatter: (value) => {
                        return value > 0 ? value + "%" : "";
                      },
                      color: "#fff",
                      font: {
                        weight: "bold",
                      },
                    },
                  },
                }}
              />
            );
          })()}
        </div>
      )}

      <hr
        className="section-divider my-4"
        style={{ borderTop: "2px solid #dee2e6" }}
      />

      {(() => {
        // Find all grades where "None" is NOT 100% in the LTI eligibility chart,
        // but are missing from all LTI datasets due to insufficient data
        const ltiEligibility = reportData.chartDataByGradingSystem.ltiEligibility;
        const ltiThreshold = reportData.chartDataByGradingSystem.ltiThresholdAnnualReward;
        const ltiTarget = reportData.chartDataByGradingSystem.ltiTargetAnnualReward;
        const ltiStretch = reportData.chartDataByGradingSystem.ltiStretchAnnualReward;

        if (
          ltiEligibility &&
          Array.isArray(ltiEligibility.labels) &&
          Array.isArray(ltiEligibility.datasets)
        ) {
          // Find the dataset index for "None"
          const noneIdx = ltiEligibility.datasets.findIndex(
            ds => ds.label && ds.label.toLowerCase().includes("none")
          );
          let gradesWithNoneNot100 = [];
          if (noneIdx !== -1) {
            gradesWithNoneNot100 = ltiEligibility.labels.filter((grade, i) => {
              const val = ltiEligibility.datasets[noneIdx].data[i];
              return Number(val) !== 100;
            });
          }

          // Collect all grades present in any LTI dataset
          const ltiGrades = new Set([
            ...(ltiThreshold?.labels || []),
            ...(ltiTarget?.labels || []),
            ...(ltiStretch?.labels || []),
          ]);

          // Filter gradesWithNoneNot100 to those NOT present in any LTI dataset
          const missingGrades = gradesWithNoneNot100.filter(
            grade => !ltiGrades.has(grade)
          );

          if (missingGrades.length > 0 && (!hiddenSections.ltiParticipationBaseSalary || !hiddenSections.ltiParticipationTGP)) {
            return (
              <div className="alert alert-warning my-2" style={{ fontSize: "0.95em" }}>
                <strong>Note:</strong> The following grades have LTI eligibility but are not shown in the LTI tables below due to insufficient LTI data:{" "}
                <span style={{ fontWeight: 700 }}>
                  {missingGrades.join(", ")}
                </span>
              </div>
            );
          }
        }
        return null;
      })()}

      {(() => {
        // Defensive: check data exists
        const threshold =
          reportData.chartDataByGradingSystem.ltiThresholdAnnualReward;
        const target =
          reportData.chartDataByGradingSystem.ltiTargetAnnualReward;
        const stretch =
          reportData.chartDataByGradingSystem.ltiStretchAnnualReward;

        // Get all grades from the labels array (shared by all datasets)
        const grades = threshold?.labels || [];

        // Table rows for percentiles and their corresponding dataset labels
        const percentiles = [
          {
            label: (
              <>
                25<sup>th</sup>
              </>
            ),
            base: "Base Salary P25",
            tgp: "TGP P25",
          },
          {
            label: (
              <>
                50<sup>th</sup>
              </>
            ),
            base: "Base Salary P50",
            tgp: "TGP P50",
          },
          {
            label: (
              <>
                75<sup>th</sup>
              </>
            ),
            base: "Base Salary P75",
            tgp: "TGP P75",
          },
          {
            label: "Average",
            base: "Base Salary Average",
            tgp: "TGP Average",
          },
        ];

        // Helper to get value from dataset by label and grade index
        function getValue(ds, label, gradeIndex) {
          const dataset = ds?.datasets?.find((d) => d.label === label);
          if (!dataset || gradeIndex === -1) return null;
          return dataset.data[gradeIndex];
        }

        // Helper to format as currency
        function formatR(val) {
          if (val == null || isNaN(val)) return "";
          return (
            "R " +
            Number(val).toLocaleString("en-ZA", {
              maximumFractionDigits: 0,
            })
          );
        }

        // Helper to build grade indices map
        function buildGradeIndicesMap(grades) {
          return grades.reduce((acc, grade, idx) => {
            if (!acc[grade]) acc[grade] = [];
            acc[grade].push(idx);
            return acc;
          }, {});
        }

        // Helper to calculate average value
        function avgValue(ds, label, indices) {
          const values = indices
            .map((gIdx) => getValue(ds, label, gIdx))
            .filter((v) => v != null && !isNaN(v));
          if (!values.length) return null;
          return (
            values.reduce((sum, v) => sum + Number(v), 0) /
            values.length
          );
        }

        const gradeIndicesMap = buildGradeIndicesMap(grades);
        const uniqueGradeNames = Object.keys(gradeIndicesMap);

        return (
          <>
            {!hiddenSections.ltiParticipationBaseSalary && (
              <>
                <h5>LTI participation based on Base salary</h5>
                {/* Base Salary Table */}
                <Table striped bordered hover responsive className="mt-3">
                  <thead>
                    <tr>
                      <th style={{ width: "120px" }}>Grade</th>
                      <th>Percentile</th>
                      <th>Threshold LTI values (Base salary)</th>
                      <th>Target LTI values (Base salary)</th>
                      <th>Stretch LTI values (Base salary)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uniqueGradeNames.map((grade) =>
                      percentiles.map((p, pIdx) => {
                        const indices = gradeIndicesMap[grade];
                        return (
                          <tr key={grade + "-base-" + pIdx}>
                            {pIdx === 0 && (
                              <td rowSpan={percentiles.length}>{grade}</td>
                            )}
                            <td>{p.label}</td>
                            <td>{formatR(avgValue(threshold, p.base, indices))}</td>
                            <td>{formatR(avgValue(target, p.base, indices))}</td>
                            <td>{formatR(avgValue(stretch, p.base, indices))}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </>
            )}

            {!hiddenSections.ltiParticipationTGP && (
              <>
                <h6 className="mt-4">
                  LTI participation based on Total guaranteed package (TGP)
                </h6>
                {/* TGP Table */}
                <Table striped bordered hover responsive className="mt-3">
                  <thead>
                    <tr>
                      <th style={{ width: "120px" }}>Grade</th>
                      <th>Percentile</th>
                      <th>Threshold LTI values (TGP)</th>
                      <th>Target LTI values (TGP)</th>
                      <th>Stretch LTI values (TGP)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uniqueGradeNames.map((grade) =>
                      percentiles.map((p, pIdx) => {
                        const indices = gradeIndicesMap[grade];
                        return (
                          <tr key={grade + "-tgp-" + pIdx}>
                            {pIdx === 0 && (
                              <td rowSpan={percentiles.length}>{grade}</td>
                            )}
                            <td>{p.label}</td>
                            <td>{formatR(avgValue(threshold, p.tgp, indices))}</td>
                            <td>{formatR(avgValue(target, p.tgp, indices))}</td>
                            <td>{formatR(avgValue(stretch, p.tgp, indices))}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </>
            )}
          </>
        );
      })()}

      <hr
        className="section-divider my-4"
        style={{ borderTop: "2px solid #dee2e6" }}
      />

      {!hiddenSections.ltiAnnualAwards && (
        <>
      {/* LTI Annual Awards
       * This section provides detailed information on the annual awards for Long-Term Incentive (LTI) plans
       * across different job grades. The data is presented in a table showing the distribution of the annual awards.
       *
       */}
      <h5>LTI annual awards by remuneration base</h5>
      <p>
        The table below shows the distribution of the annual awards for LTI.
        The base type is the basis on which the annual awards are
        calculated.
      </p>
      <Table striped bordered hover responsive className="mt-3">
        <thead>
          <tr>
            <th>LTI Remuneration Base</th>
            <th>Percentage</th>
          </tr>
        </thead>
        <tbody>
          {(() => {
            // Try to get baseTypeDistribution from reportData, fallback to empty object if not present
            const ltiBaseTypeDistribution =
              reportData?.chartDataByGradingSystem?.baseTypeDistribution
                ?.lti || {};

            // Map for display names
            const baseTypeLabels = {
              percentage_base: "Percentage of Base Pay",
              percentage_tgp: "Percentage of Total Guaranteed Package",
              monetary_value: "Monetary Value by Grade",
              other: "Other",
            };

            // If no data, show "No data available"
            const keys = Object.keys(ltiBaseTypeDistribution);
            if (!keys.length) {
              return (
                <tr>
                  <td colSpan="2" className="text-center">
                    No data available
                  </td>
                </tr>
              );
            }

            // Show all base types (even if 0%)
            // Sort keys alphabetically by base type label (or key if label not found)
            return keys
              .sort((a, b) => {
                const labelA = baseTypeLabels[a] || a;
                const labelB = baseTypeLabels[b] || b;
                return labelA.localeCompare(labelB);
              })
              .map((key) => (
                <tr key={key}>
                  <td>{baseTypeLabels[key] || key}</td>
                  <td>{Math.ceil(ltiBaseTypeDistribution[key])}%</td>
                </tr>
              ));
          })()}
        </tbody>
      </Table>
        </>
      )}

      <hr
        className="section-divider my-4"
        style={{ borderTop: "2px solid #dee2e6" }}
      />

      {!hiddenSections.ltiAllocationPercentagesByGrade && (
        <>
      <h5>LTI allocation percentages by grade</h5>
      <p>
        The table below shows the average allocation percentages per grade for LTI. Participants who entered monetary values were considered as a percentage of TGP basis to be included in these percentages. All values are rounded up to the nearest whole percentage.
      </p>
      {(() => {
        const ltiBaseTypeAvg =
          reportData?.chartDataByGradingSystem?.baseTypeAverageOfPercentages?.lti;

        if (
          !ltiBaseTypeAvg ||
          !Array.isArray(ltiBaseTypeAvg.labels) ||
          !Array.isArray(ltiBaseTypeAvg.datasets) ||
          ltiBaseTypeAvg.datasets.length === 0
        ) {
          return (
            <Table striped bordered hover responsive className="mt-3 mb-4">
              <thead>
                <tr>
                  <th>Grade</th>
                  <th>Threshold</th>
                  <th>Target</th>
                  <th>Stretch</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan="4" className="text-center">
                    No data available
                  </td>
                </tr>
              </tbody>
            </Table>
          );
        }

        // Map dataset label to its data array for easy lookup
        const datasetMap = {};
        ltiBaseTypeAvg.datasets.forEach((ds) => {
          datasetMap[ds.label] = ds.data;
        });

        // Get all unique dataset labels in the order: Threshold, Target, Stretch (if present)
        const allLabels = ltiBaseTypeAvg.datasets.map((ds) => ds.label);
        const orderedLabels = ["Threshold", "Target", "Stretch"].filter((lbl) =>
          allLabels.includes(lbl)
        );

        return (
          <Table striped bordered hover responsive className="mt-3 mb-4">
            <thead>
              <tr>
                <th>Grade</th>
                {orderedLabels.map((lbl) => (
                  <th key={lbl}>{lbl}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ltiBaseTypeAvg.labels.map((grade, idx) => (
                <tr key={grade}>
                  <td>{grade}</td>
                  {orderedLabels.map((lbl) => {
                    const value = datasetMap[lbl]?.[idx];
                    // Show as integer, rounded up, or "-" if not present
                    return (
                      <td key={lbl}>
                        {typeof value === "number" && !isNaN(value)
                          ? Math.ceil(value) + "%"
                          : "-"}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </Table>
        );
      })()}
        </>
      )}

      <hr
        className="section-divider my-4"
        style={{ borderTop: "2px solid #dee2e6" }}
      />

      {/* LTI Plans */}
      <h5 className="mt-4">LTI plan types</h5>
      <p className="text-muted">
      The graph below indicates the types of LTI plans applicable at  each eligible grade.
      </p>
      {reportData.chartDataByGradingSystem?.ltiPlans && (() => {
        const ltiPlans = reportData.chartDataByGradingSystem.ltiPlans;

        // Dynamically build a mapping from label to display label
        // Try to use a mapping if available, otherwise format label
        const planTypeLabels = {
          stock_options: "Stock/Share Options",
          sars: "Share Appreciation Rights (SARs)",
          fsp: "Forfeitable Share Plan (FSP)",
          conditional_share: "Conditional Share Plan",
          single_incentive: "Single Incentive Plan",
          value_appreciation: "Value Appreciation Rights",
          deferred_bonus: "Deferred Bonus Plan",
          other: "Other"
        };

        // Get all unique plan type keys in the order they appear in the data
        const planTypeOrder = ltiPlans.datasets.map(ds => ds.label);

        // Build datasets with dynamic color assignment and display label
        const datasets = ltiPlans.datasets.map((ds, idx) => ({
          ...ds,
          label: planTypeLabels[ds.label] || formatLegendLabel(ds.label),
          backgroundColor: CHART_PALETTE[idx % CHART_PALETTE.length],
          borderColor: CHART_PALETTE[idx % CHART_PALETTE.length],
          borderWidth: 1,
        }));

        return (
          <div style={{ height: "500px", marginBottom: "2rem", width: "100%" }}>
            <Bar
              data={{
                ...ltiPlans,
                datasets,
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                  x: {
                    stacked: true,
                    title: {
                      display: true,
                      text: "Grade",
                    },
                  },
                  y: {
                    stacked: true,
                    title: {
                      display: true,
                      text: "Percentage (%)",
                    },
                    max: 100,
                  },
                },
                plugins: {
                  legend: {
                    position: "bottom",
                    labels: {
                      boxWidth: 12,
                      generateLabels: (chart) => {
                        const datasets = chart.data.datasets;
                        return datasets.map((dataset, i) => ({
                          text: formatLegendLabel(dataset.label),
                          fillStyle: dataset.backgroundColor,
                          strokeStyle: dataset.borderColor,
                          lineWidth: dataset.borderWidth,
                          hidden: !chart.isDatasetVisible(i),
                          index: i,
                        }));
                      },
                    },
                  },
                  tooltip: {
                    callbacks: {
                      label: function (context) {
                        return `${formatLegendLabel(context.dataset.label)}: ${context.raw}%`;
                      },
                    },
                  },
                  datalabels: {
                    formatter: (value) => {
                      return value > 0 ? value + "%" : "";
                    },
                    color: "#fff",
                    font: {
                      weight: "bold",
                    },
                  },
                },
              }}
            />
          </div>
        );
      })()}

      <hr
        className="section-divider my-4"
        style={{ borderTop: "2px solid #dee2e6" }}
      />

      {/* Application of Performance Conditions */}
      <h5>LTI performance conditions applicable</h5>
      <p>
      The graph below displays the performance conditions typically linked to LTI plans and the percentage of participants who reported using them.
      </p>
      {reportData.chartDataByGradingSystem?.ltiPlansPerformanceConditions &&
        (() => {
          // Dynamically calculate the total for each category across all types
          const ltiData =
            reportData.chartDataByGradingSystem
              .ltiPlansPerformanceConditions;
          // Map label to color for legend and chart
          const labelColorMap = {
            "Performance Conditions Apply": "#003366",
            "No Performance Conditions": "#FF6B00",
          };

          // The order of these keys determines the order in the chart and legend
          const labelOrder = [
            "Performance Conditions Apply",
            "No Performance Conditions",
          ];

          // Sum up the data for each label across all types
          const categoryTotals = labelOrder.map((label) => {
            const dataset = ltiData.datasets.find(
              (ds) => ds.label === label
            );
            if (!dataset) return 0;
            // Sum all values in the data array for this dataset
            return dataset.data.reduce(
              (sum, val) => sum + (typeof val === "number" ? val : 0),
              0
            );
          });

          // Calculate the total sum for percentage calculation
          const total = categoryTotals.reduce((sum, val) => sum + val, 0);

          // Calculate percentages, rounded to nearest integer
          const percentages = categoryTotals.map((val) =>
            total > 0 ? Math.round((val / total) * 100) : 0
          );

          return (
            <div
              style={{
                height: "400px",
                marginBottom: "2rem",
                maxWidth: "600px",
                margin: "0 auto",
              }}
            >
              <Doughnut
                data={{
                  labels: [
                    "Performance conditions",
                    "No performance conditions",
                  ],
                  datasets: [
                    {
                      data: percentages,
                      backgroundColor: labelOrder.map(
                        (label) => labelColorMap[label]
                      ),
                      borderColor: labelOrder.map(
                        (label) => labelColorMap[label]
                      ),
                      borderWidth: 1,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  cutout: "65%",
                  plugins: {
                    legend: {
                      position: "bottom",
                      labels: {
                        boxWidth: 12,
                        padding: 20,
                        color: "#333",
                        font: {
                          size: 13,
                        },
                      },
                    },
                    tooltip: {
                      callbacks: {
                        label: function (context) {
                          const label = context.label || "";
                          const value = context.raw || 0;
                          return `${label}: ${value}%`;
                        },
                      },
                    },
                    datalabels: {
                      display: function (context) {
                        return context.dataset.data[context.dataIndex] > 0;
                      },
                      formatter: function (value) {
                        return value + "%";
                      },
                      color: "#fff",
                      font: {
                        size: 9,
                        weight: "bold",
                      },
                      anchor: "center",
                      align: "center",
                    },
                  },
                }}
              />
            </div>
          );
        })()}

      <hr
        className="section-divider my-4"
        style={{ borderTop: "2px solid #dee2e6" }}
      />

      {/* LTI Performance Conditions
       * This section provides detailed information on the performance conditions used for Long-Term Incentive (LTI) plans
       * across different job grades. The data is presented in a chart showing the percentage of companies that use various performance conditions for LTI allocations.
       *
       */}
      <h5>LTI performance conditions</h5>
      <p>
      The following information outlines the percentage of LTI plans that include performance conditions compared to those without applicable performance conditions.  
      </p>
      {reportData.chartDataByGradingSystem.ltiPlanDistribution && (
        <div style={{ height: "500px", marginBottom: "2rem" }}>
          <Bar
            data={{
              ...reportData.chartDataByGradingSystem.ltiPlanDistribution,
              datasets:
                reportData.chartDataByGradingSystem.ltiPlanDistribution.datasets.map(
                  (dataset, index) => ({
                    ...dataset,
                    backgroundColor: dataset.data.map(
                      (_, i) =>
                        CHART_PALETTE[
                          i % CHART_PALETTE.length
                        ]
                    ),
                    borderColor: dataset.data.map(
                      (_, i) =>
                        CHART_PALETTE[
                          i % CHART_PALETTE.length
                        ]
                    ),
                    borderWidth: 1,
                  })
                ),
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              indexAxis: "x",
              plugins: {
                legend: {
                  display: false,
                },
                tooltip: {
                  callbacks: {
                    label: function (context) {
                      return `${context.raw}%`;
                    },
                  },
                },
                datalabels: {
                  display: function (context) {
                    return context.dataset.data[context.dataIndex] > 0;
                  },
                  formatter: function (value) {
                    return value + "%";
                  },
                  color: "#fff",
                  font: {
                    size: 9,
                    weight: "bold",
                  },
                  anchor: "center",
                  align: "center",
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                  max: 100,
                  ticks: {
                    callback: function (value) {
                      return value + "%";
                    },
                  },
                },
                x: {
                  ticks: {
                    font: {
                      size: 11,
                    },
                  },
                },
              },
            }}
          />
        </div>
      )}
    

      <hr
        className="section-divider my-4"
        style={{ borderTop: "2px solid #dee2e6" }}
      />

      {/* Vesting Methodology */}
      <h5>LTI vesting methodology</h5>
      <p className="text-muted mb-3">
      The following graph illustrates the various vesting methods employed, along with the corresponding percentage of participants utilising each approach.
      </p>
      {reportData.chartDataByGradingSystem &&
        reportData.chartDataByGradingSystem.ltiVestingMethodology && (
          <div style={{ height: "500px", marginBottom: "2rem", width: "100%" }}>
            <Bar
              data={{
                ...reportData.chartDataByGradingSystem
                  .ltiVestingMethodology,
                datasets:
                  reportData.chartDataByGradingSystem.ltiVestingMethodology.datasets.map(
                    (dataset, index) => ({
                      ...dataset,
                      backgroundColor:
                        CHART_PALETTE[
                          index % CHART_PALETTE.length
                        ],
                      borderColor:
                        CHART_PALETTE[
                          index % CHART_PALETTE.length
                        ],
                      borderWidth: 1,
                    })
                  ),
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                  x: {
                    stacked: true,
                    title: {
                      display: true,
                      text: "Grade",
                    },
                  },
                  y: {
                    stacked: true,
                    title: {
                      display: true,
                      text: "Percentage (%)",
                    },
                    max: 100,
                  },
                },
                plugins: {
                  title: {
                    display: true,
                    text: "Vesting Mechanism by Grade",
                    font: {
                      size: 16,
                    },
                  },
                  legend: {
                    position: "bottom",
                    labels: {
                      generateLabels: (chart) => {
                        const datasets = chart.data.datasets;
                        return datasets.map((dataset, i) => {
                          const label = dataset.label || `Dataset ${i + 1}`;
                          const color =
                            CHART_PALETTE[
                              i % CHART_PALETTE.length
                            ];

                          return {
                            text: formatLegendLabel(label),
                            fillStyle: color,
                            strokeStyle: color,
                            lineWidth: 1,
                            hidden: !chart.isDatasetVisible(i),
                            index: i,
                            datasetIndex: i,
                          };
                        });
                      },
                    },
                  },
                  datalabels: {
                    formatter: (value) => {
                      return value > 0 ? value + "%" : "";
                    },
                    color: "#fff",
                    font: {
                      weight: "bold",
                      size: 9,
                    },
                  },
                },
              }}
            />
          </div>
        )}

      <hr
        className="section-divider my-4"
        style={{ borderTop: "2px solid #dee2e6" }}
      />

      {/* Vesting Periods by Long-term Incentive Type */}
      <h5>LTI vesting periods</h5>
      <p>
      Participants reported the vesting period by grade as illustrated in the table below.  
      </p>

      <div className="table-responsive mb-4">
        <Table striped bordered hover>
          <thead>
            <tr>
              <th style={{ width: "120px", backgroundColor: "#4CAF50", color: "white" }}>
                Vesting
              </th>
              {reportData.chartDataByGradingSystem &&
                reportData.chartDataByGradingSystem.ltiVestingPeriods &&
                reportData.chartDataByGradingSystem.ltiVestingPeriods.labels.map(
                  (label) => (
                    <th
                      key={label}
                      style={{ backgroundColor: "#4CAF50", color: "white" }}
                    >
                      {label}
                    </th>
                  )
                )}
            </tr>
          </thead>
          <tbody>
            {(() => {
              // Use the same ordering as the chart below
              const ltiVestingPeriods = reportData.chartDataByGradingSystem &&
                reportData.chartDataByGradingSystem.ltiVestingPeriods;

              if (!ltiVestingPeriods) return null;

              const desiredOrder = [
                "Annually",
                "Two Years",
                "Three Years",
                "Four Years",
                "Five Years",
                "Longer Than Five Years",
              ];

              const normalize = (label) =>
                label
                  .replace(/_/g, " ")
                  .replace(/\s+/g, " ")
                  .trim()
                  .toLowerCase();

              // Build a map from normalized label to dataset
              const datasetMap = {};
              ltiVestingPeriods.datasets.forEach(
                (dataset) => {
                  datasetMap[normalize(dataset.label)] = dataset;
                }
              );

              // Build the reordered datasets array according to desiredOrder
              const reorderedDatasets = desiredOrder
                .map((orderLabel) => datasetMap[normalize(orderLabel)])
                .filter(Boolean);

              // If not all desired rows are present, append any missing ones from the original
              const usedLabels = new Set(reorderedDatasets.map(ds => ds.label));
              ltiVestingPeriods.datasets.forEach(ds => {
                if (!usedLabels.has(ds.label)) {
                  reorderedDatasets.push(ds);
                }
              });

              return reorderedDatasets.map((dataset) => (
                <tr key={`vesting-${dataset.label}`}>
                  <td
                    style={{ backgroundColor: "#4CAF50", color: "white" }}
                  >
                    {formatLegendLabel(dataset.label)}
                  </td>
                  {dataset.data.map((value, index) => (
                    <td key={index}>{value}%</td>
                  ))}
                </tr>
              ));
            })()}
          </tbody>
        </Table>
      </div>

      {reportData.chartDataByGradingSystem &&
        reportData.chartDataByGradingSystem.ltiVestingPeriods && (() => {
          // Define the desired order for the rows (case-insensitive, ignore underscores)
          const desiredOrder = [
            "Annually",
            "Two Years",
            "Three Years",
            "Four Years",
            "Five Years",
            "Longer Than Five Years",
          ];

          // Helper to normalize label for matching
          const normalize = (label) =>
            label
              .replace(/_/g, " ")
              .replace(/\s+/g, " ")
              .trim()
              .toLowerCase();

          // Build a map from normalized label to dataset
          const datasetMap = {};
          reportData.chartDataByGradingSystem.ltiVestingPeriods.datasets.forEach(
            (dataset) => {
              datasetMap[normalize(dataset.label)] = dataset;
            }
          );

          // Build the reordered datasets array according to desiredOrder
          const reorderedDatasets = desiredOrder
            .map((orderLabel) => datasetMap[normalize(orderLabel)])
            .filter(Boolean);

          // If not all desired rows are present, append any missing ones from the original
          const usedLabels = new Set(reorderedDatasets.map(ds => ds.label));
          reportData.chartDataByGradingSystem.ltiVestingPeriods.datasets.forEach(ds => {
            if (!usedLabels.has(ds.label)) {
              reorderedDatasets.push(ds);
            }
          });

          return (
            <div style={{ height: "500px", marginBottom: "2rem", width: "100%" }}>
              <Bar
                data={{
                  ...reportData.chartDataByGradingSystem.ltiVestingPeriods,
                  datasets: reorderedDatasets.map((dataset, index) => ({
                    ...dataset,
                    backgroundColor:
                      CHART_PALETTE[
                        index % CHART_PALETTE.length
                      ],
                    borderColor:
                      CHART_PALETTE[
                        index % CHART_PALETTE.length
                      ],
                    borderWidth: 1,
                  })),
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    x: {
                      title: {
                        display: true,
                        text: "Grade",
                      },
                    },
                    y: {
                      title: {
                        display: true,
                        text: "Percentage (%)",
                      },
                      max: 100,
                    },
                  },
                  plugins: {
                    title: {
                      display: true,
                      text: "Vesting Periods by Grade",
                    },
                    legend: {
                      position: "bottom",
                      labels: {
                        generateLabels: (chart) => {
                          const datasets = chart.data.datasets;
                          return datasets.map((dataset, i) => {
                            const label = dataset.label || `Dataset ${i + 1}`;
                            const color =
                              CHART_PALETTE[
                                i % CHART_PALETTE.length
                              ];

                            return {
                              text: formatLegendLabel(label),
                              fillStyle: color,
                              strokeStyle: color,
                              lineWidth: 1,
                              hidden: !chart.isDatasetVisible(i),
                              index: i,
                            };
                          });
                        },
                      },
                    },
                    datalabels: {
                      formatter: (value) => {
                        return value > 0 ? value + "%" : "";
                      },
                      color: "#fff",
                      font: {
                        weight: "bold",
                        size: 9,
                      },
                    },
                  },
                }}
              />
            </div>
          );
        })()}

      <hr
        className="section-divider my-4"
        style={{ borderTop: "2px solid #dee2e6" }}
      />

      {/* Settlement Methodology */}
      <h5>LTI settlement methodology</h5>
      <p>
      The graph below shows how vested awards are settled.
      </p>
      {reportData.chartDataByGradingSystem &&
        reportData.chartDataByGradingSystem.ltiSettlementMethodology && (
          <div
            style={{
              height: "400px",
              marginBottom: "2rem",
              maxWidth: "600px",
              margin: "0 auto",
            }}
          >
            <Doughnut
              data={{
                ...reportData.chartDataByGradingSystem
                  .ltiSettlementMethodology,
                datasets:
                  reportData.chartDataByGradingSystem.ltiSettlementMethodology.datasets.map(
                    (dataset) => ({
                      ...dataset,
                      backgroundColor: dataset.data.map(
                        (_, i) =>
                          CHART_PALETTE[
                            i % CHART_PALETTE.length
                          ]
                      ),
                      borderColor: dataset.data.map(
                        (_, i) =>
                          CHART_PALETTE[
                            i % CHART_PALETTE.length
                          ]
                      ),
                      borderWidth: 1,
                    })
                  ),
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                cutout: "60%",
                plugins: {
                  title: {
                    display: true,
                    text: "LTI Settlement Methodology",
                    font: {
                      size: 16,
                    },
                  },
                  legend: {
                    position: "bottom",
                    labels: {
                      generateLabels: (chart) => {
                        const datasets = chart.data.datasets;
                        return (datasets[0]?.data ?? []).map((_, i) => {
                          const label = chart.data.labels?.[i] || `Segment ${i + 1}`;
                          const color = datasets[0].backgroundColor?.[i] ?? datasets[0].borderColor?.[i];
                          return {
                            text: formatLegendLabel(String(label)),
                            fillStyle: color,
                            strokeStyle: color,
                            lineWidth: 1,
                            hidden: false,
                            index: i,
                          };
                        });
                      },
                    },
                  },
                  datalabels: {
                    formatter: (value) => {
                      return value > 0 ? value + "%" : "";
                    },
                    color: "#fff",
                    font: {
                      weight: "bold",
                    },
                  },
                },
              }}
            />
          </div>
        )}
    </Accordion.Body>
  </Accordion.Item>
  );
};

export default LtiResultsSection;
